/**
 * RefluxScreenCard — iter1060 — кратък кратка проверка, който разграничава
 * болката от хранопровода от болката в гръдната стена.
 */
import React, { useEffect, useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/App";
import { AlertTriangle, ArrowRight, ChevronDown, Flame, HeartPulse, Loader2 } from "lucide-react";
import { toast } from "sonner";

const LEVEL_TONE = {
  likely: { light: "bg-amber-50 border-amber-300", dark: "bg-amber-900/25 border-amber-500/40" },
  possible: { light: "bg-sky-50 border-sky-200", dark: "bg-sky-900/20 border-sky-500/30" },
  unlikely: { light: "bg-emerald-50 border-emerald-200", dark: "bg-emerald-900/20 border-emerald-500/30" },
  no_data: { light: "bg-slate-50 border-slate-200", dark: "bg-slate-800/40 border-slate-700" },
};

const GROUP_BG = {
  timing: "Време и позиция",
  acid: "Киселинни признаци",
  extra: "Маските в гърлото и дишането",
  driver: "Какво го подклажда",
  against: "Признаци СРЕЩУ рефлукс",
};

export const RefluxScreenCard = ({ isDark }) => {
  const { API } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [sel, setSel] = useState([]);
  const [card, setCard] = useState([]);
  const [alarms, setAlarms] = useState([]);
  const [saving, setSaving] = useState(false);
  const [why, setWhy] = useState(null);

  useEffect(() => {
    axios.get(`${API}/reflux`)
      .then(({ data }) => {
        setData(data);
        setSel(data.selected || []);
        setCard(data.cardiac_selected || []);
        setAlarms(data.alarms_selected || []);
      })
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, [API]);

  const save = async () => {
    setSaving(true);
    try {
      const { data: d } = await axios.post(`${API}/reflux/screen`, {
        selected: sel, cardiac: card, alarms,
      });
      setData(d);
      toast.success(d.verdict?.label_bg || "Записано.");
    } catch { toast.error("Записът не беше запазен."); }
    finally { setSaving(false); }
  };

  const cardCls = isDark ? "bg-slate-900/60 border-slate-800" : "bg-white border-slate-200";
  const box = isDark ? "bg-slate-800/50" : "bg-slate-100";

  if (loading) {
    return <div className={`p-5 rounded-2xl border ${cardCls} flex justify-center`} data-testid="reflux-loading">
      <Loader2 className="w-5 h-5 animate-spin text-amber-500" /></div>;
  }
  if (!data) return null;
  const tone = LEVEL_TONE[data.verdict.level] || LEVEL_TONE.no_data;

  const chip = (on, onClick, label, testid, danger) => (
    <button key={testid} onClick={onClick} data-testid={testid}
            className={`text-[10px] px-2 py-1 rounded-lg border text-left ${on
              ? (danger ? "bg-rose-600 text-white border-rose-600" : "bg-amber-600 text-white border-amber-600")
              : isDark ? "border-slate-700" : "border-slate-300"}`}>
      {label}
    </button>
  );

  const groups = {};
  (data.items || []).forEach((i) => { (groups[i.group] = groups[i.group] || []).push(i); });

  return (
    <div className={`p-5 rounded-2xl border-2 ${cardCls}`} data-testid="reflux-screen-card">
      <button onClick={() => setOpen(!open)} className="w-full text-left flex items-center gap-2"
              data-testid="reflux-toggle">
        <Flame className="w-4 h-4 text-amber-500 shrink-0" />
        <h3 className="font-bold text-sm flex-1 min-w-0">Рефлукс или гръдна стена</h3>
        {data.has_screen && (
          <span className="text-[10px] px-2 py-1 rounded-md font-bold bg-amber-600 text-white shrink-0 whitespace-nowrap"
                data-testid="reflux-level-badge">{data.verdict.short_bg || data.verdict.label_bg}</span>
        )}
        <ChevronDown className={`w-4 h-4 opacity-50 shrink-0 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      <p className="text-[11px] opacity-75 leading-relaxed mt-1" data-testid="reflux-mechanism">
        {data.mechanism_bg}
      </p>

      {data.verdict.cardiac.triggered && (
        <div className={`mt-3 p-3 rounded-xl border ${isDark ? "bg-rose-900/30 border-rose-500/50" : "bg-rose-50 border-rose-300"}`}
             data-testid="reflux-cardiac-alert">
          <div className="flex items-center gap-1.5">
            <HeartPulse className="w-4 h-4 text-rose-500" />
            <span className="text-xs font-bold">Спешен център</span>
          </div>
          <div className="text-[11px] opacity-90 mt-1 leading-relaxed">{data.verdict.cardiac.text_bg}</div>
        </div>
      )}
      {data.verdict.alarms.triggered && (
        <div className={`mt-2 p-3 rounded-xl border ${isDark ? "bg-rose-900/20 border-rose-500/40" : "bg-rose-50 border-rose-200"}`}
             data-testid="reflux-alarm-alert">
          <div className="flex items-center gap-1.5">
            <AlertTriangle className="w-4 h-4 text-rose-500" />
            <span className="text-xs font-bold">Това налага гастроскопия, не режим</span>
          </div>
          <div className="text-[11px] opacity-90 mt-1 leading-relaxed">{data.verdict.alarms.text_bg}</div>
        </div>
      )}

      {open && (
        <div className="mt-4 space-y-4">
          {Object.entries(groups).map(([g, items]) => (
            <div key={g}>
              <div className="text-[11px] font-bold mb-1.5">{GROUP_BG[g] || g}</div>
              <div className="space-y-1" data-testid={`reflux-group-${g}`}>
                {items.map((i) => (
                  <div key={i.key} className={`rounded-xl ${box}`}>
                    <div className="p-2 flex items-start gap-2">
                      <button onClick={() => setSel(sel.includes(i.key)
                                ? sel.filter((k) => k !== i.key) : [...sel, i.key])}
                              data-testid={`reflux-item-${i.key}`}
                              className={`shrink-0 w-4 h-4 mt-0.5 rounded border ${sel.includes(i.key)
                                ? "bg-amber-600 border-amber-600" : isDark ? "border-slate-600" : "border-slate-400"}`}
                              aria-checked={sel.includes(i.key)} role="checkbox" />
                      <div className="flex-1 min-w-0">
                        <div className="text-[11px] font-bold leading-snug">{i.label_bg}</div>
                        <button onClick={() => setWhy(why === i.key ? null : i.key)}
                                className="text-[10px] opacity-50 hover:opacity-100"
                                data-testid={`reflux-why-${i.key}`}>защо?</button>
                        {why === i.key && (
                          <div className="text-[10px] opacity-80 mt-0.5 leading-relaxed">{i.why_bg}</div>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}

          <div>
            <div className="text-[11px] font-bold mb-1.5">Сърдечни предупредителни признаци — отбележете, ако има</div>
            <div className="flex flex-wrap gap-1.5" data-testid="reflux-cardiac-flags">
              {data.cardiac_flags.map((f) => chip(card.includes(f.key),
                () => setCard(card.includes(f.key) ? card.filter((k) => k !== f.key) : [...card, f.key]),
                f.label_bg, `reflux-cardiac-${f.key}`, true))}
            </div>
          </div>

          <div>
            <div className="text-[11px] font-bold mb-1.5">Тревожни признаци от стомаха и червата</div>
            <div className="flex flex-wrap gap-1.5" data-testid="reflux-gi-alarms">
              {data.gi_alarms.map((f) => chip(alarms.includes(f.key),
                () => setAlarms(alarms.includes(f.key) ? alarms.filter((k) => k !== f.key) : [...alarms, f.key]),
                f.label_bg, `reflux-alarm-${f.key}`, true))}
            </div>
          </div>

          <button onClick={save} disabled={saving} data-testid="reflux-save"
                  className="w-full py-2 rounded-lg bg-amber-600 text-white text-[11px] font-bold disabled:opacity-60">
            {saving ? "Разчитам…" : "Разчетете резултата"}
          </button>

          {data.has_screen && (
            <div className={`p-3 rounded-xl border ${isDark ? tone.dark : tone.light}`} data-testid="reflux-verdict">
              <div className="flex items-baseline gap-2">
                <span className="text-xs font-bold" data-testid="reflux-verdict-label">{data.verdict.label_bg}</span>
                <span className="ml-auto text-[10px] opacity-60 tabular-nums" data-testid="reflux-score">
                  {data.verdict.score} т.
                </span>
              </div>
              <div className="text-[11px] opacity-85 mt-1 leading-relaxed">{data.verdict.text_bg}</div>
              {data.verdict.positive_bg?.length > 0 && (
                <div className="text-[10px] opacity-75 mt-1.5" data-testid="reflux-positive">
                  В полза на рефлукс: {data.verdict.positive_bg.join("; ")}.
                </div>
              )}
              {data.verdict.against_bg?.length > 0 && (
                <div className="text-[10px] opacity-75 mt-1" data-testid="reflux-against">
                  Срещу рефлукс: {data.verdict.against_bg.join("; ")}.
                </div>
              )}
            </div>
          )}

          {data.ppi && (
            <button onClick={() => navigate(data.ppi.route)} data-testid="reflux-ppi"
                    className={`w-full text-left p-3 rounded-xl border ${isDark ? "border-slate-700" : "border-slate-300"}`}>
              <div className="text-[11px] font-bold">Киселинен блокер в списъка: {data.ppi.meds.join(", ")}</div>
              <div className="text-[10px] opacity-80 mt-0.5 leading-relaxed">{data.ppi.text_bg}</div>
            </button>
          )}

          <div>
            <div className="text-[11px] font-bold mb-1.5">Двуседмичният тест — без разход и дава отговор</div>
            <div className="space-y-1.5" data-testid="reflux-trial">
              {data.trial.map((t, i) => (
                <div key={i} className={`p-2.5 rounded-xl ${box}`} data-testid={`reflux-trial-${i}`}>
                  <div className="text-[11px] font-bold">{i + 1}. {t.title_bg}</div>
                  <div className="text-[10px] opacity-80 mt-0.5 leading-relaxed">{t.text_bg}</div>
                </div>
              ))}
            </div>
          </div>

          <div className="flex flex-wrap gap-1.5" data-testid="reflux-links">
            {data.links.map((l, i) => (
              <button key={i} onClick={() => navigate(l.route)} data-testid={`reflux-link-${i}`}
                      className={`text-[10px] px-2 py-1 rounded-lg border flex items-center gap-1 ${isDark ? "border-slate-700" : "border-slate-300"}`}>
                {l.twin_bg} <ArrowRight className="w-3 h-3" /> <span className="opacity-70">{l.why_bg}</span>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default RefluxScreenCard;
